"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Shield,
  Sparkles,
  Crown,
  Stethoscope,
  Brain,
  Target,
  Award,
  ArrowRight,
  Mic,
  BookOpen,
  CheckCircle2,
  Eye,
  ShieldCheck,
  Building2,
  Briefcase,
  Compass,
  Scale,
  Lock,
  Layers,
  Cpu,
  Users,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { BentoBlock } from "@/lib/blocks/bento-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";
import { CounterBlock } from "@/lib/blocks/counter-block";

const GOVERNANCE_BENTO = [
  { title: "Data Sovereignty", body: "Health, political, and operational data belong to the jurisdiction that governs it. Sovereign boundaries are architectural, not contractual — the infrastructure itself prevents the breaches that contract-based systems cannot guarantee.", icon: <ShieldCheck className="size-5" />, accent: "red" as const, span: "md" as const },
  { title: "Zero Third-Party Dependencies", body: "Nine proprietary AI/ML platforms built from the ground up. Data centers, mainframes, and supercomputing infrastructure owned and operated. The result: zero security incidents across fifteen-plus years.", icon: <Lock className="size-5" />, accent: "cyan" as const, span: "md" as const },
  { title: "Clinical Governance Override", body: "Physician authority is preserved in every AI-influenced clinical decision. The platform provides decision support. The patient retains autonomy. The governance framework enforces the hierarchy.", icon: <Stethoscope className="size-5" />, accent: "emerald" as const, span: "sm" as const },
  { title: "Audit Trails at Every Decision", body: "Every AI-influenced decision — clinical, political, operational — generates an audit trail. The trail is the evidence base for governance review and for the continuous improvement mechanism that prevents institutional repetition of error.", icon: <Eye className="size-5" />, accent: "amber-gold" as const, span: "sm" as const },
  { title: "Cross-Domain Authority", body: "AI governance is not a technology problem. It is a cross-domain problem. The integration of clinical precision, technological scale, and political sophistication is the authority that sovereign deployment requires.", icon: <Crown className="size-5" />, accent: "purple" as const, span: "wide" as const },
  { title: "Team Composition by Signal", body: "The seventy-percent operating model — majority-female teams built on performance signals rather than confidence signals — produces AI that is less likely to reproduce the biases that homogeneous teams embed.", icon: <Users className="size-5" />, accent: "coral" as const, span: "sm" as const },
  { title: "Failure Review Discipline", body: "The morbidity and mortality conference — applied to AI. Every deviation, every unexpected outcome, every algorithmic error is reviewed in a blame-free environment with the institutional authority to mandate changes.", icon: <Scale className="size-5" />, accent: "denim" as const, span: "sm" as const },
  { title: "Sovereign Validation", body: "Tested across eighteen countries, nine proprietary platforms, and over three hundred elite clients. The evidence is not aspirational — it is operational. The governance framework is proven where the margin for error is zero.", icon: <Target className="size-5" />, accent: "lime" as const, span: "md" as const },
];

const GOVERNANCE_STATS = [
  { value: 0, label: "Security Incidents", description: "15+ years sovereign-grade", accent: "cyan" as const, icon: <ShieldCheck className="size-5" /> },
  { value: 9, label: "Proprietary Platforms", description: "Zero third-party dependencies", accent: "purple" as const, icon: <Cpu className="size-5" /> },
  { value: 18, label: "Countries", description: "Sovereign-scale deployment", accent: "emerald" as const, icon: <GlobeIcon className="size-5" /> },
  { value: 100, suffix: "%", label: "Client Satisfaction", description: "Across 300+ elite clients", accent: "amber-gold" as const, icon: <Trophy className="size-5" /> },
];

const FAQ_ITEMS = [
  { q: "What is \"AI Governance Lessons\" about?", a: "The signature keynote on AI governance drawn from sovereign-scale operations. The argument is that AI governance is not a technology problem — it is a cross-domain problem that requires the integration of clinical precision, technological sovereignty, and political sophistication. The lessons are drawn from fifteen-plus years of zero security incidents across eighteen countries." },
  { q: "What makes this AI governance keynote different?", a: "Most AI governance keynotes focus on regulatory frameworks or technical safeguards. This keynote focuses on the sovereign architecture that has produced verified outcomes — zero security incidents, one-hundred-percent client satisfaction, nine proprietary platforms with zero third-party dependencies. The lessons are operational, not theoretical." },
  { q: "What is data sovereignty in practice?", a: "Data sovereignty means the data belongs to the jurisdiction — not to the platforms that process it. In practice: zero third-party dependencies, owned infrastructure, sovereign boundaries for data processing, and the architectural prevention of breaches that contract-based systems cannot guarantee." },
  { q: "How long is the standard keynote?", a: "Forty-five to sixty minutes for the standard keynote. Fireside chat runs thirty to forty-five minutes. Workshop runs two to three hours. Executive briefing runs sixty to ninety minutes. All formats are available virtually and in person." },
  { q: "What audiences benefit most from this keynote?", a: "Government AI policy forums, sovereign technology deployments, healthcare AI governance committees, defense AI conferences, financial services regulatory gatherings, and any organization deploying AI in environments where the margin for error is zero and the consequences of failure are national." },
  { q: "How does this relate to specific AI regulations like the EU AI Act?", a: "The keynote presents the sovereign architecture that produces compliance as a byproduct of structural design. When the architecture demands sovereignty, regulations become easier to satisfy. When the architecture permits external dependencies, regulations become harder to satisfy. The sovereign approach is the operating condition that produces regulatory sustainability." },
];

function GlobeIcon({ className }: { className?: string }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>;
}

function Trophy({ className }: { className?: string }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" /></svg>;
}

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const FOUR_LAYERS = [
  {
    num: "01",
    icon: Scale,
    title: "Constraint Definition",
    body: "Every AI platform operates within defined constraints: the data it can access, the decisions it can make autonomously, the actions that require human authorization, and the boundaries it cannot cross. The governance architecture begins with precise constraint definition — the process of determining, for each platform, what it must do, what it may do, and what it must never do.",
    accent: "cyan",
  },
  {
    num: "02",
    icon: Lock,
    title: "Constraint Enforcement",
    body: "Constraints that exist only in documentation are not constraints — they are aspirations. The governance architecture enforces constraints through automated mechanisms: real-time monitoring, automated alerts when constraint violations are imminent, and automated shutdowns that prevent violations when human intervention is not available.",
    accent: "purple",
  },
  {
    num: "03",
    icon: Target,
    title: "Outcome Verification",
    body: "AI systems produce outputs. Outcomes are the consequences of those outputs. The governance architecture includes outcome verification mechanisms that validate whether AI outputs produce the intended outcomes — creating a feedback loop that continuously improves the system&apos;s accuracy and reliability.",
    accent: "emerald",
  },
  {
    num: "04",
    icon: ShieldCheck,
    title: "Accountability Tracing",
    body: "Every AI-generated decision is traceable — from the final action through the AI system&apos;s processing to the original data inputs and human authorization. The accountability architecture produces audit trails that are cryptographically verifiable, tamper-proof, and available for review at any time.",
    accent: "amber-gold",
  },
];

const THREE_PRINCIPLES = [
  { title: "Constraint Enforcement", body: "AI systems operate within their designed boundaries — the operational limits, the jurisdictional restrictions, the data access parameters, and the decision-making authorities that define their operating envelope. The governance architecture enforces these constraints in real time, with automated alerts and automated shutdowns.", accent: "cyan" },
  { title: "Outcome Verification", body: "AI systems can produce outputs that appear correct but produce harmful outcomes. The governance architecture includes feedback mechanisms that validate AI outputs against real-world results, creating a continuous improvement cycle that catches divergence before it produces consequences.", accent: "purple" },
  { title: "Accountability Architecture", body: "Every AI-generated decision can be traced to a specific system, a specific set of inputs, a specific set of constraints, and a specific human decision-maker who authorized the action. The architecture is not optional. It is the operating condition.", accent: "amber-gold" },
];

const EVIDENCE = [
  { metric: "0", suffix: "", label: "Security Incidents", sub: "15+ years sovereign-grade", accent: "cyan" },
  { metric: "18", suffix: "", label: "Jurisdictions", sub: "Cross-jurisdictional AI", accent: "purple" },
  { metric: "99.9999", suffix: "%", label: "Uptime", sub: "31.5 sec max/year", accent: "emerald" },
  { metric: "100", suffix: "%", label: "Client Satisfaction", sub: "300+ elite clients", accent: "amber-gold" },
];

const TAKEAWAYS = [
  { title: "Governance is architecture, not documentation.", body: "The organizations that govern AI successfully have built the operational infrastructure that enforces governance principles through automated mechanisms — real-time monitoring, constraint enforcement, outcome verification, accountability tracing.", accent: "cyan" },
  { title: "Ethical AI requires operational constraint systems.", body: "Principles do not constrain AI systems. Automated constraint mechanisms do. The keynote reveals the specific architecture — constraint definition, enforcement, and verification — that transforms ethical aspirations into operational boundaries.", accent: "purple" },
  { title: "Cross-jurisdictional AI demands sovereignty-aware governance.", body: "AI systems operating across countries encounter different legal frameworks. The governance architecture must accommodate this complexity without compromising consistency — and the keynote reveals the specific mechanisms that achieve this balance.", accent: "emerald" },
  { title: "Outcome verification closes the governance loop.", body: "AI systems can produce outputs that appear correct but produce harmful outcomes. The governance architecture must include feedback mechanisms that validate AI outputs against real-world results.", accent: "amber-gold" },
  { title: "Accountability is non-negotiable at sovereign scale.", body: "Every AI-generated decision must be traceable. The keynote reveals the audit architecture that makes this traceability possible — applied across eighteen jurisdictions and over three hundred elite clients.", accent: "red" },
];

const AUDIENCE = [
  { icon: Building2, title: "AI Governance Conferences", body: "Practitioners, policymakers, and technologists addressing the challenges of governing AI systems at scale. The operational perspective fills the gap most governance conferences exhibit.", accent: "cyan" },
  { icon: Cpu, title: "Technology Conferences", body: "Major technology events where AI deployment, AI scaling, and AI governance intersect. The keynote provides the governance framework that technical teams need to implement responsible AI at scale.", accent: "purple" },
  { icon: Crown, title: "Government & Sovereign", body: "Decision-makers operating at the level where AI governance is not a policy debate but an operational requirement — where AI systems inform decisions about national security, public governance, and citizen services.", accent: "emerald" },
  { icon: Shield, title: "Defense & Security", body: "Organizations focused on national security, intelligence operations, and defense coordination. The governance architecture that has protected sovereign-grade AI operations with zero security incidents.", accent: "amber-gold" },
  { icon: Briefcase, title: "Corporate AI Leadership", body: "Executive gatherings of C-suites and boards seeking to implement AI governance — where the audience needs operational frameworks, not aspirational principles.", accent: "red" },
  { icon: BookOpen, title: "University & Academic Forums", body: "Institutions seeking to expose students, researchers, and faculty to the operational reality of AI governance — the bridge between classroom theory and operational practice.", accent: "denim" },
];

const FORMATS = [
  { label: "Keynote Address", duration: "45–60 min", body: "Concentrated operational insight on AI governance — from the core argument through the governance architecture to actionable takeaways.", accent: "cyan" },
  { label: "Fireside Chat", duration: "30–45 min", body: "Conversational depth on AI governance for intimate leadership gatherings, AI governance retreats, and executive forums.", accent: "purple" },
  { label: "Panel Participation", duration: "20–30 min", body: "Contribution to multi-speaker discussions on AI governance, AI ethics, or technology policy.", accent: "emerald" },
  { label: "Workshop / Masterclass", duration: "2–4 hrs", body: "Deep-dive AI governance training applying the governance architecture to specific AI infrastructure.", accent: "amber-gold" },
  { label: "Executive Briefing", duration: "60–90 min", body: "Private session for leadership teams seeking to implement AI governance within their organizations.", accent: "red" },
  { label: "Virtual Delivery", duration: "All formats", body: "All formats available for virtual events with the same operational authority and engagement quality.", accent: "denim" },
];

const GAINS = [
  "Governance architecture design — the layered approach: constraint definition, constraint enforcement, outcome verification, accountability tracing",
  "Cross-jurisdictional governance — the specific mechanisms that accommodate regulatory complexity without compromising consistency",
  "Human-AI accountability design — the tiered approach from fully automated to human-in-the-loop to human-on-the-loop",
  "AI ethics operationalization — how to transform ethical principles into operational constraints through automated mechanisms",
  "AI governance metrics and measurement — the specific metrics that indicate whether the governance architecture is functioning",
  "Crisis governance protocols — the architecture that governs AI systems during crises when normal processes are too slow",
];

export default function AIGovernanceLessonsPage() {
  return (
    <>
      <PageHero
        icon={Shield}
        overline="Additional Keynote"
        overlineClassName="text-cyan"
        title={
          <>
            Where AI Theory Ends and <span className="text-gradient-premium">AI Governance at Scale Begins.</span>
          </>
        }
        subtitle="AI Governance: Lessons from Operating AI Systems Across Countries is the keynote on the operational reality of governing AI platforms across multiple jurisdictions, where the consequences of governance failure are national and the margin for error is zero. The specific architecture, the tested protocols, the verified outcomes that govern AI deployed at the highest levels of consequence."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Speaking", href: "/speaking/" }, { label: "AI Governance Lessons" }]}
        badge="Architecture · Not documentation"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
            <Sparkles className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Abstract</span>
          </div>
          <h2 className="text-display-md mb-5">AI governance is survival, not compliance.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The world produces AI ethics frameworks at accelerating speed. Boards commission AI governance policies. Legislatures draft AI regulation. Conferences convene panels on responsible AI. Meanwhile, AI systems operate at sovereign scale across jurisdictions, under governance architectures that most theorists have never seen, producing outcomes that most policymakers cannot measure.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The keynote closes this gap — presenting the governance architecture for AI platforms operating across multiple jurisdictions, the ethical frameworks that prevent AI systems from producing unintended consequences at sovereign scale, the operational discipline of maintaining AI accountability, and the lessons learned from a decade of sovereign-grade operations. The audience leaves with operational governance frameworks — not aspirational principles, but tested systems that have produced verified outcomes under the most demanding conditions on earth.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Scale className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Evidence</span>
          </div>
          <h2 className="text-display-md mb-4">Sovereign-scale AI operations.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The operational metrics that define the governance architecture. The evidence that the constraint-based approach produces verified outcomes at the highest levels of consequence.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {EVIDENCE.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-md font-bold ${a.text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Governance Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">How sovereign-scale AI platforms are governed.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four layers — each addressing a specific dimension of AI governance, each tested through sovereign-scale operations, each integrated into the unified governance system.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {FOUR_LAYERS.map((l, i) => {
            const a = ACCENT[l.accent];
            return (
              <motion.div
                key={l.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className="flex items-start gap-4 mb-3">
                    <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg ring-2 ${a.ring} shrink-0`}>
                      <l.icon className="size-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-caption font-bold ${a.text} opacity-60 mb-1`}>Layer {l.num}</div>
                      <h3 className={`text-heading-md ${a.text}`}>{l.title}</h3>
                    </div>
                  </div>
                  <p className="text-body-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: l.body }} />
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
              <ShieldCheck className="size-3.5 text-purple" />
              <span className="text-overline text-purple">The Ethical Framework</span>
            </div>
            <h2 className="text-display-md mb-4">Responsible AI deployment as an operating system.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The ethical framework is not an aspirational document. It is an operational system — tested across eighteen countries, validated through fifteen-plus years of sovereign-grade operations, and proven through zero security incidents and one-hundred-percent client satisfaction. The framework operates on three principles: constraint enforcement, outcome verification, and accountability architecture.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              These three principles are not abstract ideals. They are automated mechanisms that operate continuously, with real-time monitoring, automated alerts, and outcome verification loops that improve the system with every cycle. The audience learns to translate ethical principles into operational constraints — the specific architecture that makes ethical AI a reality, not a promise.
            </p>
          </div>
          <div className="space-y-3">
            {THREE_PRINCIPLES.map((p, i) => {
              const a = ACCENT[p.accent];
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                      <span className="text-caption font-bold text-white">{i + 1}</span>
                    </div>
                    <div>
                      <h3 className={`text-heading-sm mb-1 ${a.text}`}>{p.title}</h3>
                      <p className="text-caption text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: p.body }} />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <CheckCircle2 className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Key Takeaways</span>
          </div>
          <h2 className="text-display-md mb-4">Five insights for AI governance.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The minimum viable governance framework. Specific, implementable, drawn from sovereign-scale operational evidence.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-3 max-w-5xl mx-auto">
          {TAKEAWAYS.map((t, i) => {
            const accents = ["cyan", "purple", "emerald", "amber-gold", "red"];
            const a = ACCENT[accents[i % accents.length]];
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <span className="text-caption font-bold text-white">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-sm mb-1 ${a.text}`}>{t.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{t.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Users className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Who This Keynote Serves</span>
          </div>
          <h2 className="text-display-md mb-4">Ideal audience.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The keynote serves the decision-makers who determine how AI systems are deployed and governed — across technology, government, defense, and corporate contexts.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {AUDIENCE.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <a.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-sm mb-1.5 ${ac.text}`}>{a.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{a.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Briefcase className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">Format Options</span>
            </div>
            <h2 className="text-display-md mb-4">Flexibility for every event.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Six delivery formats. Customization begins with a diagnostic consultation: understanding the audience&apos;s AI infrastructure, their governance maturity, their jurisdictional requirements, and their desired governance outcomes.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {FORMATS.map((f, i) => {
              const a = ACCENT[f.accent];
              return (
                <motion.div
                  key={f.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className={`text-heading-sm ${a.text}`}>{f.label}</h3>
                    <span className={`text-caption font-bold ${a.text}`}>{f.duration}</span>
                  </div>
                  <p className="text-caption text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: f.body }} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Shield className="size-10 text-cyan mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Book this keynote.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              AI Governance: Lessons from Operating AI Systems Across Countries bridges the gap between AI governance theory and AI governance operations. The architecture that has produced zero security incidents across fifteen-plus years of sovereign-grade AI operations — applied to the audience&apos;s specific AI context.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Book the Keynote
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/speaking/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <BookOpen className="size-4" />
                All Speaking Topics
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
            <Cpu className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Signature Framework</span>
          </div>
          <h2 className="text-display-md mb-4">The Governance Framework Bento.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            Eight components of the sovereign AI governance framework. Each component is a structural element of the architecture. Each component is verified by sovereign-scale outcomes. The integration of all eight is the system that produces zero security incidents across fifteen-plus years.
          </p>
        </div>
        <BentoBlock items={GOVERNANCE_BENTO} />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <ShieldCheck className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Sovereign Evidence</span>
          </div>
          <h2 className="text-display-md mb-4">The outcomes that define sovereign AI governance.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The argument is not aspirational. It is structural. The evidence is drawn from sovereign-scale operations where the margin for error is zero and the consequences of failure are national.
          </p>
        </div>
        <CounterBlock stats={GOVERNANCE_STATS} columns={4} />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-5`}>
            <Lock className="size-3.5 text-red" />
            <span className="text-overline text-red">The Problem This Keynote Solves</span>
          </div>
          <h2 className="text-display-md mb-4">The structural failure of conventional AI governance.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The keynote addresses a structural failure in conventional AI governance: the assumption that regulatory compliance produces security. The evidence contradicts this assumption. The most consequential data breaches in recent history have occurred in systems that satisfied regulatory frameworks. The compliance was achieved. The security was not.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The root cause is the architecture that compliance frameworks evaluate. Most AI deployments depend on third-party processing — cloud infrastructure, external APIs, shared services. Each dependency is a potential vector for breach. Each contract with a third party is a trust assumption. When the trust assumption fails, the architecture that was compliant becomes the architecture that is breached. Compliance did not produce security. Compliance produced the appearance of security.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The keynote solves this by presenting the sovereign alternative: an architecture that produces security as a structural property rather than a contractual property. The sovereign approach is not anti-regulation. It is the operating condition that makes regulation meaningful. When the data is sovereign by design, the regulation is satisfied by architecture. When the data is not sovereign, the regulation is satisfied by paperwork — and the paperwork does not stop the breach. The sovereign architecture has produced zero security incidents across fifteen-plus years. The conventional architecture has produced breaches. The difference is structural.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <AccordionBlock
          items={FAQ_ITEMS}
          eyebrow="Frequently Asked Questions"
          title="What decision-makers ask before booking."
          description="The questions that surface most often in preliminary conversations. Each answer reflects the operating reality, not the marketing narrative."
        />
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "The Triple Threat", description: "The cross-domain foundation — medicine, technology, and politics as one operating method.", href: "/speaking/the-triple-threat/", icon: Layers, accent: "amber-gold" },
          { title: "How Excellence Actually Runs", description: "The operational excellence foundation that AI governance requires — protocols, checklists, reviews, accountability.", href: "/speaking/how-excellence-actually-runs/", icon: Award, accent: "red" },
          { title: "Build in the Dark", description: "The discretion doctrine that sovereign-scale AI governance demands — the operating condition under which the most sensitive work is performed.", href: "/speaking/build-in-the-dark/", icon: Eye, accent: "denim" },
          { title: "Future of Women&apos;s Health", description: "AI governance applied to clinical contexts — the integration of physician expertise, technology, and regulatory discipline.", href: "/speaking/future-of-womens-health/", icon: Stethoscope, accent: "lime" },
        ]}
      />
    </>
  );
}
